import { useMemo, useState, type FormEvent } from 'react';
import type { Activity, ActivityStatus } from '@otakotakdev/shared';
import { formatMinutes } from '@otakotakdev/shared';
import {
  useActivities,
  useCreateActivity,
  useUpdateActivity,
  useDeleteActivity,
  useGoals,
} from '@/hooks/api';
import { Badge, EmptyState, ErrorState, ListSkeleton, Modal, PageHeader, Spinner } from '@/components/ui';

const STATUS_OPTIONS: ActivityStatus[] = ['planned', 'in_progress', 'completed', 'cancelled'];

const STATUS_TONE: Record<ActivityStatus, 'default' | 'accent' | 'success' | 'danger'> = {
  planned: 'default',
  in_progress: 'accent',
  completed: 'success',
  cancelled: 'danger',
};

const WEEK_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

interface ActivityFormState {
  title: string;
  description: string;
  activityDate: string;
  durationMinutes: string;
  status: ActivityStatus;
  goalId: string;
}

const emptyForm = (today: string): ActivityFormState => ({
  title: '',
  description: '',
  activityDate: today,
  durationMinutes: '',
  status: 'planned',
  goalId: '',
});

function lastDayOfMonth(ym: string): string {
  const [y, m] = ym.split('-').map(Number) as [number, number];
  const d = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const mm = String(m).padStart(2, '0');
  return `${y}-${mm}-${String(d).padStart(2, '0')}`;
}

function monthLabel(ym: string): string {
  const [y, m] = ym.split('-').map(Number) as [number, number];
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function shiftMonth(ym: string, delta: number): string {
  const [y, m] = ym.split('-').map(Number) as [number, number];
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

function weekdayMon0(dateStr: string): number {
  const [y, m, d] = dateStr.split('-').map(Number) as [number, number, number];
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return dow === 0 ? 6 : dow - 1;
}

function ActivityForm({
  open,
  initial,
  goals,
  onClose,
  onSaved,
}: {
  open: boolean;
  initial: Activity | null;
  goals: { id: string; title: string }[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState<ActivityFormState>(
    initial
      ? {
          title: initial.title,
          description: initial.description,
          activityDate: initial.activityDate,
          durationMinutes: initial.durationMinutes != null ? String(initial.durationMinutes) : '',
          status: initial.status,
          goalId: initial.goalId ?? '',
        }
      : emptyForm(today),
  );
  const create = useCreateActivity();
  const update = useUpdateActivity();
  const mutation = initial ? update : create;
  const error = mutation.error;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    const duration = form.durationMinutes ? Number(form.durationMinutes) : null;
    const payload = {
      title: form.title.trim(),
      description: form.description,
      activityDate: form.activityDate,
      durationMinutes: Number.isFinite(duration as number) ? duration : null,
      goalId: form.goalId || null,
      status: form.status,
    };
    if (initial) {
      update.mutate({ id: initial.id, ...payload }, { onSuccess: onSaved });
    } else {
      create.mutate({ ...payload, source: 'dashboard' }, { onSuccess: onSaved });
    }
  };

  return (
    <Modal open={open} title={initial ? 'Edit activity' : 'New activity'} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label htmlFor="act-title" className="label">Title</label>
          <input id="act-title" className="input" required maxLength={200} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </div>
        <div>
          <label htmlFor="act-desc" className="label">Description</label>
          <textarea id="act-desc" className="input min-h-20" maxLength={4000} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="act-date" className="label">Date</label>
            <input id="act-date" type="date" className="input" required value={form.activityDate} onChange={(e) => setForm({ ...form, activityDate: e.target.value })} />
          </div>
          <div>
            <label htmlFor="act-duration" className="label">Duration (minutes)</label>
            <input id="act-duration" type="number" min={0} max={1440} className="input" value={form.durationMinutes} onChange={(e) => setForm({ ...form, durationMinutes: e.target.value })} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="act-status" className="label">Status</label>
            <select id="act-status" className="input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as ActivityStatus })}>
              {STATUS_OPTIONS.map((s) => (<option key={s} value={s}>{s}</option>))}
            </select>
          </div>
          <div>
            <label htmlFor="act-goal" className="label">Related goal</label>
            <select id="act-goal" className="input" value={form.goalId} onChange={(e) => setForm({ ...form, goalId: e.target.value })}>
              <option value="">None</option>
              {goals.map((g) => (<option key={g.id} value={g.id}>{g.title}</option>))}
            </select>
          </div>
        </div>
        {error && (<p role="alert" className="text-sm text-danger">{error instanceof Error ? error.message : 'Failed to save activity'}</p>)}
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={mutation.isPending || !form.title.trim()}>
            {mutation.isPending ? <Spinner label="Saving..." /> : initial ? 'Save changes' : 'Create'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="card px-4 py-3">
      <p className="text-xs uppercase tracking-wide text-muted">{label}</p>
      <p className="mt-1 text-lg font-semibold tabular-nums">{value}</p>
      {hint && <p className="text-xs text-muted">{hint}</p>}
    </div>
  );
}

export function ActivitiesPage() {
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [month, setMonth] = useState(() => today.slice(0, 7));
  const [statusFilter, setStatusFilter] = useState<ActivityStatus | ''>('');
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Activity | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Activity | null>(null);

  const monthStart = `${month}-01`;
  const monthEnd = lastDayOfMonth(month);

  const params: Record<string, unknown> = { limit: 100, sort: 'activity_date', from: monthStart, to: monthEnd };
  if (statusFilter) params.status = statusFilter;

  const { data, isLoading, isError, error, refetch } = useActivities(params);
  const goalsQuery = useGoals({ status: 'active', limit: 100 });
  const update = useUpdateActivity();
  const remove = useDeleteActivity();
  const goals = goalsQuery.data?.items ?? [];
  const activities = data?.items ?? [];

  const byDate = useMemo(() => {
    const m = new Map<string, Activity[]>();
    for (const a of activities) {
      const arr = m.get(a.activityDate);
      if (arr) arr.push(a);
      else m.set(a.activityDate, [a]);
    }
    return m;
  }, [activities]);

  const monthStats = useMemo(() => {
    const total = activities.length;
    const completed = activities.filter((a) => a.status === 'completed').length;
    const minutes = activities.reduce((s, a) => s + (a.durationMinutes ?? 0), 0);
    const planned = activities.filter((a) => a.status === 'planned').length;
    return { total, completed, minutes, planned };
  }, [activities]);

  const selectedActivities = selectedDate ? (byDate.get(selectedDate) ?? []) : [];
  const listActivities = selectedDate ? selectedActivities : activities;

  const gridCells = useMemo(() => {
    const firstDow = weekdayMon0(monthStart);
    const daysInMonth = Number(monthEnd.slice(8, 10));
    const cells: (string | null)[] = [];
    for (let i = 0; i < firstDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const ds = `${month}-${String(d).padStart(2, '0')}`;
      cells.push(ds);
    }
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [month, monthStart, monthEnd]);

  const isCurrentMonth = month === today.slice(0, 7);

  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader
        title="Activities"
        subtitle={`${monthLabel(month)} \u00B7 ${monthStats.total} activities \u00B7 ${formatMinutes(monthStats.minutes)} logged`}
        actions={
          <button type="button" className="btn-primary" onClick={() => { setEditing(null); setFormOpen(true); }}>
            + New activity
          </button>
        }
      />

      <div className="mb-4 grid grid-cols-3 gap-3">
        <Stat label="This month" value={String(monthStats.total)} hint={`${monthStats.completed} completed`} />
        <Stat label="Time logged" value={formatMinutes(monthStats.minutes)} hint="sum of durations" />
        <Stat label="Planned" value={String(monthStats.planned)} hint="still to do" />
      </div>

      <div className="grid gap-4 lg:grid-cols-[1.45fr_0.95fr]">
        <section className="card p-4" aria-labelledby="cal-heading">
          <div className="mb-3 flex items-center justify-between gap-2">
            <h2 id="cal-heading" className="text-sm font-semibold">{monthLabel(month)}</h2>
            <div className="flex items-center gap-1.5">
              <button type="button" className="btn-ghost px-2.5 py-1.5 text-sm" onClick={() => setMonth((m) => shiftMonth(m, -1))} aria-label="Previous month">&lt;</button>
              <button type="button" className="btn-ghost px-2.5 py-1.5 text-xs" onClick={() => { setMonth(today.slice(0, 7)); setSelectedDate(today); }} disabled={isCurrentMonth && selectedDate === today}>Today</button>
              <button type="button" className="btn-ghost px-2.5 py-1.5 text-sm" onClick={() => setMonth((m) => shiftMonth(m, 1))} aria-label="Next month">&gt;</button>
            </div>
          </div>

          <div className="mb-2 grid grid-cols-7 gap-1 text-center text-xs text-muted" role="row">
            {WEEK_LABELS.map((w) => (<span key={w} className="py-1 font-medium">{w}</span>))}
          </div>

          {isLoading ? (<ListSkeleton rows={3} />) : (
            <div className="grid grid-cols-7 gap-1" role="grid" aria-label="Activity calendar">
              {gridCells.map((dateStr, idx) => {
                if (!dateStr) return <div key={`e-${idx}`} className="min-h-[68px] rounded-lg sm:min-h-[78px]" />;
                const dayActs = byDate.get(dateStr) ?? [];
                const isToday = dateStr === today;
                const isSelected = dateStr === selectedDate;
                const count = dayActs.length;
                const minutes = dayActs.reduce((s, a) => s + (a.durationMinutes ?? 0), 0);
                const hasCompleted = dayActs.some((a) => a.status === 'completed');
                const hasPlanned = dayActs.some((a) => a.status === 'planned');
                const dayNum = Number(dateStr.slice(8, 10));
                return (
                  <button
                    key={dateStr}
                    type="button"
                    role="gridcell"
                    aria-selected={isSelected}
                    aria-label={`${dateStr}, ${count} activities${minutes ? `, ${formatMinutes(minutes)}` : ''}`}
                    onClick={() => setSelectedDate((prev) => (prev === dateStr ? null : dateStr))}
                    className={[
                      'flex min-h-[68px] flex-col rounded-lg border px-1.5 py-1 text-left transition sm:min-h-[78px] sm:px-2 sm:py-1.5',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
                      isSelected ? 'border-accent bg-accent/10' : isToday ? 'border-accent/40 bg-white/[0.03]' : 'border-white/5 bg-white/[0.02] hover:bg-white/[0.06]',
                    ].join(' ')}
                  >
                    <span className={['inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium tabular-nums', isSelected ? 'bg-accent text-surface' : isToday ? 'bg-white text-surface' : 'text-ink'].join(' ')}>{dayNum}</span>
                    <span className="mt-auto flex flex-wrap gap-1">
                      {count > 0 ? (
                        <>
                          <span className="inline-flex items-center rounded-full bg-white/10 px-1.5 py-0.5 text-[11px] leading-none text-ink">{count}</span>
                          {minutes > 0 && (<span className="hidden text-[11px] leading-none text-muted sm:inline">{formatMinutes(minutes)}</span>)}
                        </>
                      ) : (<span className="text-[11px] text-white/20">-</span>)}
                    </span>
                    {count > 0 && (
                      <span className="mt-1 flex gap-1" aria-hidden>
                        <span className={`h-1.5 w-1.5 rounded-full ${hasCompleted ? 'bg-success' : 'bg-white/15'}`} />
                        <span className={`h-1.5 w-1.5 rounded-full ${hasPlanned ? 'bg-warning' : 'bg-white/15'}`} />
                        <span className={`h-1.5 w-1.5 rounded-full ${count > 2 ? 'bg-accent' : 'bg-white/15'}`} />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-muted">
            <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-success" /> completed</span>
            <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-warning" /> planned</span>
            <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-accent" /> 3+ items</span>
          </div>
        </section>

        <section className="card flex min-h-[280px] flex-col p-4" aria-labelledby="day-heading">
          <div className="mb-3 flex items-center justify-between gap-2">
            <h2 id="day-heading" className="text-sm font-semibold">
              {selectedDate ? new Date(selectedDate + 'T12:00:00Z').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC' }) : 'Select a date'}
            </h2>
            {selectedDate && (<button type="button" className="btn-ghost px-2 py-1 text-xs" onClick={() => setSelectedDate(null)}>Show all</button>)}
          </div>

          {!selectedDate ? (
            <div className="flex flex-1 flex-col justify-center py-6 text-center">
              <p className="text-sm text-muted">Pick a day on the calendar to see what happened.</p>
              <p className="mt-1 text-xs text-muted">{monthStats.total === 0 ? 'No activities this month yet.' : `${monthStats.total} activities this month. Choose a highlighted day.`}</p>
              {monthStats.total === 0 && (<button type="button" className="btn-primary mx-auto mt-4" onClick={() => { setEditing(null); setFormOpen(true); }}>Log activity</button>)}
            </div>
          ) : selectedActivities.length === 0 ? (
            <div className="flex flex-1 flex-col justify-center py-6 text-center">
              <p className="text-sm font-medium">No activities on {selectedDate}</p>
              <p className="mt-1 text-xs text-muted">Nothing logged for this day.</p>
              <button type="button" className="btn-primary mx-auto mt-4" onClick={() => { setEditing(null); setFormOpen(true); }}>Add for {selectedDate.slice(5)}</button>
            </div>
          ) : (
            <>
              <p className="mb-3 text-xs text-muted">{selectedActivities.length} {selectedActivities.length === 1 ? 'activity' : 'activities'} &middot; {formatMinutes(selectedActivities.reduce((s, a) => s + (a.durationMinutes ?? 0), 0))}</p>
              <ul className="space-y-2">
                {selectedActivities.map((a) => (
                  <li key={a.id} className="rounded-lg border border-white/5 bg-white/[0.03] px-3 py-2.5">
                    <div className="flex items-start justify-between gap-2">
                      <p className="min-w-0 flex-1 truncate text-sm font-medium">{a.title}</p>
                      <Badge tone={STATUS_TONE[a.status]}>{a.status}</Badge>
                    </div>
                    {a.description && <p className="mt-1 line-clamp-2 text-xs text-muted">{a.description}</p>}
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted">
                      {a.durationMinutes != null && <span>{formatMinutes(a.durationMinutes)}</span>}
                      {a.goalId && <Badge>linked to goal</Badge>}
                    </div>
                    <div className="mt-2 flex gap-1.5">
                      {a.status !== 'completed' && (<button type="button" className="btn-ghost px-2 py-1 text-xs" onClick={() => update.mutate({ id: a.id, status: 'completed', endedAt: new Date().toISOString() })}>Complete</button>)}
                      <button type="button" className="btn-ghost px-2 py-1 text-xs" onClick={() => { setEditing(a); setFormOpen(true); }}>Edit</button>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <label htmlFor="act-status-filter" className="text-xs font-medium text-muted">Filter</label>
        <select id="act-status-filter" className="input w-auto min-h-[44px]" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as ActivityStatus | '')} aria-label="Filter by status">
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map((s) => (<option key={s} value={s}>{s}</option>))}
        </select>
        {selectedDate && (
          <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs">
            {selectedDate}
            <button type="button" className="rounded-full bg-white/10 px-2 py-0.5 text-xs hover:bg-white/15" onClick={() => setSelectedDate(null)} aria-label="Clear date filter">Clear</button>
          </span>
        )}
        <span className="ml-auto text-xs text-muted">{selectedDate ? `${listActivities.length} on ${selectedDate}` : `${activities.length} this month`}</span>
      </div>

      {isError && (<div className="mt-4"><ErrorState message={error instanceof Error ? error.message : 'Failed to load activities'} onRetry={() => void refetch()} /></div>)}

      {!isLoading && !isError && listActivities.length === 0 && !selectedDate && activities.length === 0 && (
        <div className="mt-4"><EmptyState title="No activities" hint="Log what you are working on to build a record of your time. It will appear on the calendar above." action={<button type="button" className="btn-primary mt-2" onClick={() => { setEditing(null); setFormOpen(true); }}>Log activity</button>} /></div>
      )}

      {!isLoading && !isError && listActivities.length > 0 && (
        <div className="card mt-4 divide-y divide-white/5">
          <div className="flex items-center justify-between px-4 py-3">
            <h3 className="text-sm font-semibold">{selectedDate ? `Activities on ${selectedDate}` : `All in ${monthLabel(month)}`}</h3>
            <span className="text-xs text-muted">{listActivities.length} items</span>
          </div>
          {listActivities.map((activity) => (
            <div key={activity.id} className="flex flex-wrap items-center gap-2 px-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{activity.title}</p>
                <p className="text-xs text-muted">{activity.activityDate}{activity.durationMinutes != null && ` \u00B7 ${formatMinutes(activity.durationMinutes)}`}{activity.goalId && ' \u00B7 linked to goal'}</p>
                {activity.description && <p className="mt-1 line-clamp-2 text-xs text-muted">{activity.description}</p>}
              </div>
              <Badge tone={STATUS_TONE[activity.status]}>{activity.status}</Badge>
              <div className="flex gap-1.5">
                {activity.status !== 'completed' && (<button type="button" className="btn-ghost px-2 py-1 text-xs" onClick={() => update.mutate({ id: activity.id, status: 'completed', endedAt: new Date().toISOString() })}>Complete</button>)}
                <button type="button" className="btn-ghost px-2 py-1 text-xs" onClick={() => { setEditing(activity); setFormOpen(true); }}>Edit</button>
                <button type="button" className="btn-danger px-2 py-1 text-xs" onClick={() => setConfirmDelete(activity)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {formOpen && (<ActivityForm key={editing?.id ?? 'new'} open={formOpen} initial={editing} goals={goals} onClose={() => setFormOpen(false)} onSaved={() => setFormOpen(false)} />)}

      <Modal open={confirmDelete !== null} title="Delete activity?" onClose={() => setConfirmDelete(null)}>
        <p className="mb-4 text-sm text-muted">Delete <span className="text-ink">{confirmDelete?.title}</span>? This cannot be undone.</p>
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={() => setConfirmDelete(null)}>Cancel</button>
          <button type="button" className="btn-danger" disabled={remove.isPending} onClick={() => { if (!confirmDelete) return; remove.mutate(confirmDelete.id, { onSuccess: () => setConfirmDelete(null) }); }}>Delete</button>
        </div>
      </Modal>
    </div>
  );
}
